import { Card } from "@/src/components/wrappers/Card";
import { AmenityDetails, Booking } from "../model";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetch as apiFetch } from "@/src/lib/fetch";
import { Button } from "@/src/components/ui/button";
import { InnerCard } from "@/src/components/wrappers/InnerCard";
import { Amenity } from "../../Yachts/model";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Plus, Trash } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../components/ui/form";
import { Checkbox } from "../../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { BookingExtrasForm } from "./extras.schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { calculateCurrencyPrice } from "../../../lib/utils";
import { z } from "zod";
import { useToast } from "@/src/components/ui/use-toast";
import { PencilIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { useEmit, useSubscribe } from "../../../lib/actions";
import { useBookingExtrasMutation } from "../../../hooks/useBookingExtrasMutation";
import { useBookingData } from "../../../hooks/useBookingData";
import { useBookingsStore } from "../../../stores/bookingsStore";
import { useOffersStore } from "../../../stores/offersStore";
import {
  calculateDaysStarted,
  calculateWeeksStarted,
} from "../../Offers/EditOffer/ChangeYachtDetailsModal/ChangeYachtDetailsModal";

export const EquipmentAndExtras = ({
  booking,
}: {
  booking: Booking;
}) => {
  return (
    <React.Fragment>
      <AmenitiesAndExtrasSection booking={booking} />
    </React.Fragment>
  );
};

type ExtrasInvoiceState = {
  currency: string | null;
  items: { id: string; text: string; price: number; qty: number; currency: string | null; paidLocally: boolean; invoiceable: boolean; invoice: { id: string; number: string | null; at: string | null } | null }[];
  pending: { count: number; total: number; ids: string[] };
  canInvoice: boolean;
};

// Price input that tolerates a decimal point mid-edit: a controlled numeric value
// re-renders "1683." straight back to "1683", eating the keystroke — so extras
// prices could never carry pennies (a 2.5% card fee of 1,683.50 got stored 1,683).
// Holds the raw string locally and emits the parsed number (max 2dp).
const DecimalPriceInput = ({
  value,
  onNumber,
  className,
  placeholder,
}: {
  value: number;
  onNumber: (n: number) => void;
  className?: string;
  placeholder?: string;
}) => {
  const [raw, setRaw] = useState(value || value === 0 ? String(value) : "");
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) {
      setRaw(value || value === 0 ? String(value) : "");
      last.current = value;
    }
  }, [value]);
  return (
    <Input
      className={className}
      placeholder={placeholder}
      inputMode="decimal"
      value={raw}
      onChange={(e) => {
        const v = e.target.value;
        if (!/^\d*[.,]?\d{0,2}$/.test(v)) return;
        setRaw(v);
        const n = parseFloat(v.replace(",", "."));
        const parsed = isNaN(n) ? 0 : n;
        last.current = parsed;
        onNumber(parsed);
      }}
    />
  );
};

const AmenitiesAndExtrasSection = ({ booking }: { booking: Booking }) => {
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [addExtraOpen, setAddExtraOpen] = useState(false);
  const { booking: bookingData, setBooking } = useBookingsStore();
  const { currencies } = useOffersStore();

  const { removeAmenity, updateExtras, createExtra } = useBookingExtrasMutation(booking);
  const { amenitiesDetailsData } = useBookingData(booking);
  const offerCurrencyCode = booking?.currency?.code;
  const yachtCurrencyCode = booking?.offerYacht?.yacht?.amenities?.find((am) => !!am.currency)?.currency?.code || "EUR";

  const yachtLocalAmenitiesCurrency = useMemo(
    () =>
      booking.offerYacht.yacht.amenities
        .filter((am) => am.paid_locally)
        .map((am) => am.currency?.code)
        .filter((v, i, a) => a.indexOf(v) === i)[0] ??
      booking.offerYacht.yacht.amenities.find((am) => !!am.currency)?.currency?.code ??
      "EUR",
    [booking.offerYacht.yacht.amenities, currencies],
  );

  const extras = amenitiesDetailsData.filter((a) => !a.included && a.checked);
  const equipment = bookingData?.amenitiesDetails
    ?.filter((am) => (am.included || (am.price === 0 && !!am.isCustomAmenity)) && am.freeOfCharge !== true)
    ?.sort((a) => {
      if (a.isCustomAmenity) {
        return 1;
      } else {
        return -1;
      }
    });
  console.log(amenitiesDetailsData);
  useEffect(() => {
    setBooking(booking);
  }, []);

  useSubscribe(
    "booking:update",
    (data: AmenityDetails[]) =>
      !!bookingData &&
      setBooking({
        ...bookingData,
        offerYacht: {
          ...bookingData.offerYacht,
          amenitiesDetails: data,
        },
      }),
  );

  const onNewExtraAdded = async (data: NewExtra) => {
    let days = 1,
      weeks = 1;
    const startDate = bookingData?.startDate ?? bookingData?.offerYacht?.startDate;
    const endDate = bookingData?.endDate ?? bookingData?.offerYacht?.endDate;
    if (startDate && endDate) {
      days = calculateDaysStarted(startDate, endDate);
      weeks = calculateWeeksStarted(startDate, endDate);
    }

    const mult = ["per day", "per night"].includes(data.per) ? days : ["per week"].includes(data.per) ? weeks : 1;
    const currencyId = currencies.find((c) => c.code === data.currency)?.id;

    await createExtra([
      {
        ...data,
        paid_locally: data.paidLocally,
        paid: !!data.price,
        comment: "",
        included: false,
        text: data.text,
        isPercentage: false,
        manuallyAssigned: false,
        number: 1,
        percentageType: "fixed",
        id: "",
        currencyId: currencyId || booking.currency?.id,
        offerYachtId: booking.offerYacht.id,
        dedupeKey: "",
        provider: "Helm",
        currencyCode: data.currency || booking.currency?.code,
        isCustomAmenity: true,
        per: data.per,
        finalBookingPrice: data.price * mult,
        price: data.price * mult,
        yachtId: booking.offerYacht.yacht.id,
        yachtIndex: undefined,
        checked: true,
      } as any,
    ]).then((res) => {
      const amenities = bookingData?.offerYacht?.yacht?.amenities || [];
      const newExtra = res?.data?.[0];

      bookingData &&
        setBooking({
          ...bookingData,
          offerYacht: {
            ...bookingData?.offerYacht,
            amenitiesDetails: [
              ...bookingData?.amenitiesDetails,
              {
                id: newExtra?.id,
                price: newExtra?.price * mult,
                checked: true,
                obligatory: newExtra?.obligatory,
                paidLocally: newExtra?.paid_locally,
                included: newExtra?.included,
                text: newExtra.text,
                number: newExtra.number,
                provider: newExtra.provider,
                isCustomAmenity: true,
                finalBookingPrice: data.price * mult,
                per: data.per,
                hidden: data?.hidden,
                freeOfCharge: data?.freeOfCharge,
              },
            ],
            yacht: {
              ...bookingData?.offerYacht?.yacht,
              amenities: [...amenities, newExtra],
            },
          },
        });
    });
    setAddExtraOpen(false);
  };

  useSubscribe("booking:new-extra", onNewExtraAdded);

  const removeAm = (amenity: AmenityDetails, bookingId: string) => {
    removeAmenity({ id: amenity.id, bookingId }).then(() => {
      updateExtras(booking.amenitiesDetails?.filter((am) => am !== amenity));
    });
  };

  // Design (ws-booking.js "Line items"): the charter fee and the extra
  // services as a priced table — equipment that comes with the boat isn't a
  // line item and stays out of the way (it's on the yacht details).
  const money = (v: number, code?: string | null) => new Intl.NumberFormat("en-EN", { style: "currency", currency: code || "EUR" }).format(v || 0);
  const bookingCcy = booking.currency?.code || "EUR";
  const start = booking.startDate ?? booking.offerYacht?.startDate, finish = booking.endDate ?? booking.offerYacht?.endDate;
  const nights = start && finish ? Math.round((new Date(finish).getTime() - new Date(start).getTime()) / 86400000) : null;
  const duration = nights == null ? null : nights % 7 === 0 ? `${nights / 7} week${nights === 7 ? "" : "s"}` : `${nights} night${nights === 1 ? "" : "s"}`;
  // pricing.discountPrice is the discount AMOUNT, not the discounted price: the
  // charter fee the client pays is clientPrice (list price less discount).
  const pr: any = booking.pricing ?? {};
  const charterFee = Number(pr.clientPrice) || (Number(pr.originalPrice) && Number(pr.discountPrice) ? Number(pr.originalPrice) - Number(pr.discountPrice) : 0) || Number(pr.originalPrice) || 0;
  const rows = [
    ...(charterFee > 0 ? [{ key: "fee", extraId: null as string | null, item: "Charter fee", detail: [booking.offerYacht?.yacht?.name, duration].filter(Boolean).join(" · "), qty: 1, unit: charterFee, total: charterFee, ccy: bookingCcy, muted: false }] : []),
    ...(extras ?? []).map((e, i) => {
      const qty = Number(e.number) > 0 ? Number(e.number) : 1;
      const ccy = e.paidLocally ? yachtCurrencyCode : bookingCcy;
      const detail = [e.obligatory ? "obligatory" : "optional", e.paidLocally ? "paid locally at the base" : "billed with the charter", e.isCustomAmenity ? "custom" : null].filter(Boolean).join(" · ");
      return { key: `x${i}`, extraId: e.id, item: e.text || "Extra", detail, qty, unit: qty > 1 ? e.price / qty : e.price, total: e.price, ccy, muted: !e.price };
    }),
  ];
  const clientTotal = Number(booking.pricing?.totalPrice) || rows.filter((r) => r.ccy === bookingCcy).reduce((t, r) => t + r.total, 0);
  // Which extras are on a Xero invoice already, and the action for the rest.
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: inv } = useQuery({ queryKey: ["extrasInvoice", booking.id], queryFn: () => apiFetch<{ data: ExtrasInvoiceState }>(`/api/finance/bookings/${booking.id}/extras-invoice`).then((r: any) => r.data as ExtrasInvoiceState), enabled: !!booking.id });
  const invById = new Map((inv?.items ?? []).map((i) => [i.id, i]));
  const [invoicing, setInvoicing] = useState(false);
  const raiseInvoice = async () => {
    if (!inv || !inv.pending.count) return;
    if (!window.confirm(`Raise a Xero invoice for ${inv.pending.count} extra${inv.pending.count === 1 ? "" : "s"} (${money(inv.pending.total, inv.currency)}) to the client, due in 7 days?`)) return;
    setInvoicing(true);
    try {
      const r: any = await apiFetch(`/api/finance/bookings/${booking.id}/extras-invoice`, "POST", { extraIds: inv.pending.ids });
      toast({ title: "Invoice raised in Xero", description: `${r.data.invoice.number ?? r.data.invoice.id} · ${money(r.data.invoice.total, r.data.invoice.currency)} · due ${r.data.invoice.dueDate}` });
      queryClient.invalidateQueries();
    } catch (e: any) {
      toast({ title: "Couldn't raise the invoice", description: e?.message, variant: "destructive" });
    } finally { setInvoicing(false); }
  };
  // APA as a line item: a percentage of the charter fee, billed with the charter.
  const addApa = () => {
    const raw = window.prompt("APA as a percentage of the charter fee", "30");
    const pct = Number(raw);
    if (!raw || !Number.isFinite(pct) || pct <= 0 || charterFee <= 0) return;
    onNewExtraAdded({ id: "", text: `APA (${pct}% of charter fee)`, price: Math.round((charterFee * pct) / 100 * 100) / 100, per: "per booking", currency: bookingCcy, paidLocally: false, obligatory: true, hidden: false, freeOfCharge: false });
  };
  const localTotal = rows.filter((r) => r.ccy !== bookingCcy).reduce((t, r) => t + r.total, 0);

  return (
    <Card className="w-full flex flex-col gap-y-4 !p-0">
      <div className="flex items-center gap-3 border-b border-slate-100 px-6 pt-5 pb-3">
        <h3 className="text-lg font-semibold">Line items</h3>
        <div className="ml-auto flex items-center gap-2">
          {charterFee > 0 && !rows.some((r) => /\bAPA\b/i.test(r.item)) && <Button size="sm" variant="outline" onClick={addApa}>Add APA</Button>}
          <AddExtraModal
            key={`${addExtraOpen}`}
            localCurrencyCode={yachtLocalAmenitiesCurrency}
            open={addExtraOpen}
            onOpenChange={(open) => {
              setAddExtraOpen(open);
            }}
          />
          {bookingData && (
            <EditExtrasModal
              key={extrasOpen ? "opened" : "closed"}
              offerCurrencyCode={offerCurrencyCode}
              yachtCurrencyCode={yachtCurrencyCode}
              booking={{
                ...bookingData,
                offerYacht: {
                  ...bookingData?.offerYacht!,
                  amenitiesDetails: amenitiesDetailsData,
                },
              }}
              open={extrasOpen}
              onOpen={(open) => {
                setExtrasOpen(open);
              }}
            />
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup><col className="w-[40%]" /><col /><col className="w-14" /><col className="w-36" /><col className="w-10" /></colgroup>
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-6 py-2 font-semibold">Item</th>
              <th className="py-2 font-semibold">Detail</th>
              <th className="py-2 text-right font-semibold">Qty</th>
              <th className="py-2 pr-6 text-right font-semibold">Total</th>
              <th className="w-10 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-4 text-slate-500">No extras on this booking yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.key} className={`border-t border-slate-100 ${r.muted ? "opacity-60" : ""}`}>
                <td className="px-6 py-2.5 pr-4 font-medium">{r.item}</td>
                <td className="py-2.5 pr-4 text-slate-500">
                  {r.detail}
                  {r.key !== "fee" && invById.get(r.extraId ?? "")?.invoiceable && (
                    invById.get(r.extraId ?? "")?.invoice ? (
                      <a className="ml-2 rounded-full bg-green-50 px-2 py-0.5 text-[11px] text-green-700 underline-offset-2 hover:underline" href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${invById.get(r.extraId ?? "")!.invoice!.id}`} target="_blank" rel="noreferrer noopener">
                        {invById.get(r.extraId ?? "")!.invoice!.number ?? "invoiced"}
                      </a>
                    ) : (
                      <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">not invoiced</span>
                    )
                  )}
                </td>
                <td className="py-2.5 text-right tabular-nums">{r.qty}</td>
                <td className="py-2.5 pr-6 text-right font-semibold tabular-nums" title={r.qty > 1 ? `${money(r.unit, r.ccy)} each` : undefined}>{r.total ? money(r.total, r.ccy) : "—"}</td>
                <td className="py-2.5 pr-3 text-right">{r.key !== "fee" && <button type="button" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Edit extras" onClick={() => setExtrasOpen(true)}><PencilIcon className="h-3.5 w-3.5" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-6 py-3">
        <span className="text-xs text-slate-500">Pulled from the offer · advance extras are billed with the charter, local extras are paid at the base</span>
        {inv?.canInvoice && <Button size="sm" disabled={invoicing} onClick={raiseInvoice}>{invoicing ? "Raising…" : `Invoice ${inv.pending.count} extra${inv.pending.count === 1 ? "" : "s"} · ${money(inv.pending.total, inv.currency)}`}</Button>}
        <span className="text-sm font-semibold">
          Total {money(clientTotal, bookingCcy)}
          {localTotal > 0 && <span className="ml-2 font-normal text-slate-500">+ {money(localTotal, yachtCurrencyCode)} locally</span>}
        </span>
      </div>
    </Card>
  );
};

export const EditExtrasModal = ({
  open,
  onOpen,
  booking,
  offerCurrencyCode,
  yachtCurrencyCode,
}: {
  booking: Booking;
  open: boolean;
  onOpen: (open: boolean) => void;
  offerCurrencyCode: string;
  yachtCurrencyCode: string;
}) => {
  const toast = useToast();
  const { updateExtras: updateExtrasMutation, createExtra } = useBookingExtrasMutation(booking);
  const { amenitiesDetailsData } = useBookingData(booking);
  const { setBooking, booking: bookingData } = useBookingsStore();
  const { currencies } = useOffersStore();

  const includedAmenities = booking?.amenitiesDetails?.filter((am) => am.included);
  const initialAmenitiesRef = useRef<AmenityDetails[]>([]);

  const form = useForm<BookingExtrasForm>({
    resolver: zodResolver(BookingExtrasForm),
    mode: "onTouched",
    defaultValues: {
      // Legacy bookings carry extras rows with no text (the pre-July NS blank-label
      // bug, frozen at booking time) — zod then failed the WHOLE form and Save
      // silently did nothing. Coerce so old data can always be edited.
      amenitiesDetails: amenitiesDetailsData?.filter((am) => !am.included).map((am) => ({ ...am, text: am.text ?? "Extra" })),
    },
    reValidateMode: "onChange",
  });

  const emit = useEmit<AmenityDetails[]>("booking:update");

  useEffect(() => {
    const filtered = (amenitiesDetailsData?.filter((am) => !am.included) || []).map((am) => ({
      ...am,
      text: am.text ?? "Extra",
    }));
    initialAmenitiesRef.current = filtered;
    form.reset({
      amenitiesDetails: filtered,
    });
  }, [amenitiesDetailsData]);

  const yachtCurrency = currencies?.find((c) => c.code === yachtCurrencyCode);
  const offerCurrency = currencies?.find((c) => c.code === (offerCurrencyCode ?? "EUR"));

  const calculateCurrency = (amount?: number) => {
    if (!amount) return 0;
    return calculateCurrencyPrice(offerCurrency!, yachtCurrency!, amount ?? 0);
  };

  const calculateBasedPrice = (amount?: number) => {
    if (!amount) return 0;
    return calculateCurrencyPrice(yachtCurrency!, offerCurrency!, amount ?? 0);
  };

  const calculateOfferPrice = (amount: number, currency = booking.currency?.code) => {
    return new Intl.NumberFormat("en-EN", { style: "currency", currency }).format(amount);
  };

  const calculateExtraPrice = (am: any) => {
    const people = booking.guests || booking.offer.guests;
    const location = booking.startLocation?.names ?? booking.offerYacht.startLocation?.names ?? "";
    const startDate = bookingData?.startDate ?? bookingData?.offerYacht?.startDate;
    const endDate = bookingData?.endDate ?? bookingData?.offerYacht?.endDate;
    const duration = startDate && endDate ? calculateDaysStarted(startDate, endDate) : 7;
    const clientPrice = booking.pricing?.clientPriceApi ?? 0;
    return calculateOfferPrice(
      (function (location, duration, clientPrice, listPrice, people, varies) {
        const checkName = (a: string[], v: string) => v.split("\n").some((n) => a.includes(n));
        const fn = eval(
          `(startLocation, duration, people, clientPrice, listPrice, varies) => {\n\n${am.formula}\n\n};`,
        );
        const res = fn(location, duration, people, clientPrice, listPrice, varies);
        return res;
      })(location, duration, clientPrice, clientPrice, people, 1),
      am.currency,
    );
  };

  const matchingBookingAmenity = (id: string) => initialAmenitiesRef.current?.find((a) => a.id === id);

  const updateExtras = useCallback(
    form.handleSubmit(
      (data) => {
        const amenitiesDetails = data?.amenitiesDetails?.map((am, i) => {
          const matchedAmenity = matchingBookingAmenity(am.id)!;
          return {
            ...am,
            isPriceChanged: !!(am.price !== matchedAmenity!?.price),
            isCheckChanged: !!(am.checked !== matchedAmenity?.checked),
            isPaidLocallyChanged: !!(am.paidLocally !== matchedAmenity?.paidLocally),
            isCustomAmenity: am.isCustomAmenity,
            text: am.text,
            number: am.number,
            formula: am.formula,
          };
        });

        updateExtrasMutation([...amenitiesDetails, ...includedAmenities]);
        emit(amenitiesDetails);
        onOpen(false);
      },
      () => {
        // Validation failure used to be console-only — the broker saw a dead
        // Save button. Say so.
        toast.toast({
          description: "Couldn't save — one of the extras has invalid data. Check the highlighted rows.",
          variant: "destructive",
        });
        console.error("FORM ERROR", form.formState.errors, form.getValues());
      },
    ),
    [form, bookingData],
  );

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger className="mt-4 ml-auto">
        <Button className="gap-2">
          Edit Extras <Plus size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className={`sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto overflow-x-hidden`}>
        <DialogHeader>
          <DialogTitle className="text-3xl flex flex-col">Add or edit extras</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <div className="flex flex-col gap-6">
            <h4 className="text-xl font-semibold mb-2">Extra services</h4>
            <FormField<BookingExtrasForm, "amenitiesDetails">
              name="amenitiesDetails"
              render={({ field }) => (
                <FormItem className="grid grid-cols-2 gap-x-2">
                  {field.value?.map((item, i) => (
                    <FormItem key={i} className="flex flex-col !my-0 px-4 py-3">
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            className="w-6 h-6"
                            checked={!!field.value.find((am: any) => am.id === item.id)?.checked}
                            onCheckedChange={(checked) => {
                              const values = field.value.map((v) => {
                                if (item.id === v.id) {
                                  const hidden = checked ? false : v.hidden || false;
                                  return { ...v, checked: checked, hidden };
                                } else {
                                  return v;
                                }
                              });
                              field.onChange(values);
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal text-base leading-6">{item?.text}</FormLabel>
                      </div>
                      {!item.isCustomAmenity && (
                        <span className="text-slate-700 text-sm !mt-1 ml-9">+ {calculateExtraPrice(item)}</span>
                      )}
                      <span className="pl-8 text-xs">Paid</span>
                      <RadioGroup
                        className="pl-8 flex flex-row justify-between"
                        defaultValue={field.value[i].paidLocally ? "paid_locally" : "in_advance"}
                        onValueChange={(value) => {
                          field.onChange([
                            ...field.value.map((am) => {
                              if (am.id === item.id) {
                                const paidLocally = value === "paid_locally";
                                const price = paidLocally ? calculateBasedPrice(am.price) : calculateCurrency(am.price);
                                const currency = paidLocally ? yachtCurrencyCode : (booking.currency?.code ?? "EUR");
                                const text = am.text;

                                return {
                                  ...am,
                                  price,
                                  currency,
                                  paidLocally,
                                  text,
                                };
                              } else {
                                return am;
                              }
                            }),
                          ]);
                        }}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="paid_locally" id={item.id + "1paid"} />
                          <Label htmlFor={item.id + "1paid"}>Paid locally</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="in_advance" id={item.id + "2paid"} />
                          <Label htmlFor={item.id + "2paid"}>Paid in advance</Label>
                        </div>
                      </RadioGroup>
                      <span className="pl-8 text-xs">Obligatory</span>
                      <RadioGroup
                        className="pl-8 flex flex-row justify-between"
                        defaultValue={!!field.value[i].obligatory ? "obligatory" : "nonobligatory"}
                        onValueChange={(value) => {
                          field.onChange([
                            ...field.value.map((am) => {
                              if (am.id === item.id) {
                                return {
                                  ...am,
                                  obligatory: value === "obligatory" ? true : false,
                                };
                              } else {
                                return am;
                              }
                            }),
                          ]);
                        }}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="obligatory" id={item.id + "1obligatory"} />
                          <Label htmlFor={item.id + "1obligatory"}>Obligatory</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="nonobligatory" id={item.id + "2obligatory"} />
                          <Label htmlFor={item.id + "2obligatory"}>Nonobligatory</Label>
                        </div>
                      </RadioGroup>
                      <span className="pl-8 text-xs">Visibility</span>
                      <RadioGroup
                        className="pl-8 flex flex-row justify-between"
                        defaultValue={!!field.value[i].hidden ? "hidden" : "visible"}
                        value={!!field.value[i].hidden ? "hidden" : "visible"}
                        onValueChange={(value) => {
                          field.onChange([
                            ...field.value.map((am) => {
                              if (am.id === item.id) {
                                return {
                                  ...am,
                                  hidden: value === "hidden" ? true : false,
                                  checked: value === "hidden" ? false : am.checked,
                                };
                              } else {
                                return am;
                              }
                            }),
                          ]);
                        }}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="hidden" id={item.id + "hidden"} />
                          <Label htmlFor={item.id + "hidden"}>Hidden</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="visible" id={item.id + "visible"} />
                          <Label htmlFor={item.id + "visible"}>Visible</Label>
                        </div>
                      </RadioGroup>
                      <span className="pl-8 text-xs">Free of Charge</span>
                      <RadioGroup
                        className="pl-8 flex flex-row justify-between"
                        defaultValue={!!field.value[i].freeOfCharge ? "yes" : "no"}
                        value={!!field.value[i].freeOfCharge ? "yes" : "no"}
                        onValueChange={(value) => {
                          field.onChange([
                            ...field.value.map((am) => {
                              if (am.id === item.id) {
                                return {
                                  ...am,
                                  freeOfCharge: value === "yes",
                                  price: value === "yes" ? 0 : am.price,
                                  finalBookingPrice: value === "yes" ? 0 : am.price,
                                };
                              } else {
                                return am;
                              }
                            }),
                          ]);
                        }}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id={item.id + "yes"} />
                          <Label htmlFor={item.id + "yes"}>Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id={item.id + "no"} />
                          <Label htmlFor={item.id + "no"}>No</Label>
                        </div>
                      </RadioGroup>
                      <span className="pl-8 text-xs pt-2">Final price for booking</span>
                      <div className="relative ml-8">
                        <span className="absolute right-[calc(100%+8px)] top-1/2 -translate-y-1/2">
                          {field.value[i].paidLocally ? yachtCurrency?.symbol : offerCurrency?.symbol}
                        </span>
                        <DecimalPriceInput
                          className="w-20"
                          value={field.value[i].price}
                          onNumber={(n) => {
                            field.onChange([
                              ...field.value.map((am) => {
                                if (am.id === item.id) {
                                  return {
                                    ...am,
                                    price: n,
                                  };
                                } else {
                                  return am;
                                }
                              }),
                            ]);
                          }}
                        />
                      </div>
                    </FormItem>
                  ))}
                </FormItem>
              )}
            />
          </div>
        </Form>
        <DialogFooter className="pt-6 relative before:h-[10px] before:left-[50%] before:translate-x-[-50%] before:content-[''] before:w-[100vw] before:absolute before:top-0 before:shadow-[-1px_-5px_6px_0px_rgba(0,0,0,0.05)]">
          <Button onClick={updateExtras} type="submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const schema = z.object({
  id: z.string(),
  text: z.string().min(3),
  price: z.number(),
  per: z.string(),
  currency: z.string(),
  paidLocally: z.boolean(),
  obligatory: z.boolean(),
  hidden: z.boolean(),
  freeOfCharge: z.boolean(),
});

export type NewExtra = z.infer<typeof schema>;

export const AddExtraModal = ({
  onOpenChange,
  open,
  localCurrencyCode,
  index,
  hideBtn,
}: {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  localCurrencyCode: string;
  index?: number;
  hideBtn?: boolean;
}) => {
  const { currencies } = useOffersStore();

  const form = useForm<NewExtra>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: "",
      text: "",
      price: 0,
      currency: localCurrencyCode,
      per: "per trip",
      paidLocally: true,
      obligatory: false,
      hidden: false,
      freeOfCharge: false,
    },
  });
  const emit = useEmit<any>("booking:new-extra");

  useEffect(() => {
    form.reset({
      id: "",
      text: "",
      price: 0,
      currency: localCurrencyCode,
      per: "per trip",
      paidLocally: true,
      obligatory: false,
      hidden: false,
      freeOfCharge: false,
    });
  }, [localCurrencyCode]);

  const currency = form.watch("currency");
  const currencySymbol = useMemo(
    () => currencies?.find((c) => c.code === currency)?.symbol || "",
    [currency, currencies],
  );

  const onSubmit = useCallback(
    form.handleSubmit(
      (data: NewExtra) => {
        emit({
          ...data,
          id: crypto.randomUUID(),
          text: data.text[0].toUpperCase() + data.text.slice(1),
          checked: true,
          yachtIndex: index,
        });
      },
      () => {
        console.error("FORM ERROR", form.formState.errors, form.getValues());
      },
    ),
    [form],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!hideBtn && (
        <DialogTrigger className="mt-4 ml-auto">
          <Button variant="outline" className="gap-2">
            Add new extra <Plus size={16} />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className={`sm:max-w-[480px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-3xl flex flex-col">Add extra</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <div>
            <FormField
              name="text"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel className="text-slate-600">Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div>
            <FormField
              name="currency"
              render={({ field }) => (
                <FormItem className="col-span-1 relative">
                  <FormLabel className="text-slate-600">Currency</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>
          <div className="gap-8 grid grid-cols-3">
            <FormField
              name="price"
              render={({ field }) => (
                <FormItem className="col-span-1 relative">
                  <FormLabel className="text-slate-600">Price</FormLabel>
                  <span className="absolute left-[calc(100%+4px)] bottom-[8px]">{currencySymbol}</span>
                  <FormControl className="">
                    <DecimalPriceInput placeholder="Price" value={field.value} onNumber={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="per"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel className="text-slate-600">Per</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Per" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per trip">Per trip</SelectItem>
                        <SelectItem value="per week">Per week</SelectItem>
                        <SelectItem value="per day">Per day</SelectItem>
                        <SelectItem value="per night">Per night</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <FormField
              name="paidLocally"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Paid</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(v) => field.onChange(v === "true")}
                      className="flex flex-row gap-6"
                      defaultValue={field.value.toString()}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="locally" />
                        <Label htmlFor="locally">Paid locally</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="advance" />
                        <Label htmlFor="advance">Paid in advance</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="obligatory"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Obligatory</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(v) => field.onChange(v === "true")}
                      className="flex flex-row gap-6"
                      defaultValue={field.value.toString()}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="obligatory" />
                        <Label htmlFor="obligatory">Obligatory</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="nonobligatory" />
                        <Label htmlFor="nonobligatory">Nonobligatory</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="freeOfCharge"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Free of Charge</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(v) => {
                        if (v === "true") {
                          field.onChange(true);
                          form.setValue("price", 0);
                        }
                      }}
                      className="flex flex-row gap-6"
                      defaultValue={field.value.toString()}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="yes" />
                        <Label htmlFor="yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="no" />
                        <Label htmlFor="no">No</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button onClick={onSubmit} type="submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
